import { Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Sort } from '@angular/material/sort';
import { MatTabChangeEvent } from '@angular/material/tabs';
import isEqual from 'lodash/isEqual';
import { BehaviorSubject, combineLatest, finalize, Observable, of, Subject, Subscription } from 'rxjs';
import { debounceTime, filter, map, startWith, tap } from 'rxjs/operators';

import { ApiParams, CoreApiService } from '../../core-api.service';
import { GearService } from '../gear.service';
import { LoggingService } from '../../logging.service';
import { NotificationService } from '../../notification.service';
import { ProfileService } from '../../profile/profile.service';
import { ProjectsService } from '../../projects/projects.service';
import { SiteRole } from '../../shared/enums/site-role.enum';
import { GearPermissions, GearSeries } from '../../shared/models/gear.model';
import { Option } from '../../shared/models/option.model';
import { ProjectWithStats } from '../../shared/models/project.model';
import { Project } from '../../shared/models/project.model';
import { User } from '../../shared/models/user.model';
import { replayValue } from '../../shared/observable.utils';
import { queryResource } from '../../shared/filter.utils';
import { generateElementId } from '../../shared/string.utils';

export interface ProjectRow {
  _id: string
  label: string
  group: string
}

export interface UserRow {
  _id: string
  user: string
}

interface TrackedItem {
  label: string
  selected: boolean
}

interface TrackedItems {
  projects: Record<string, TrackedItem>
  users: Record<string, TrackedItem>
}

export interface TrackedGroups {
  all: TrackedItems
  filtered: TrackedItems
}

interface CachedValue {
  isRestricted: boolean
  projects: Record<string, boolean>
  users: Record<string, boolean>
}

@Component({
  host: {
    class: 'mat-dialog-component',
    'test-id': 'gear-access-dialog',
  },
  selector: 'gear-access-dialog',
  styleUrls: ['./gear-access-dialog.component.scss'],
  templateUrl: './gear-access-dialog.component.html',
  standalone: false,
})
export class GearAccessDialogComponent implements OnDestroy, OnInit {
  checkAllState = {
    projects: { all: false, some: false },
    users: { all: false, some: false },
  }
  elementId = generateElementId()
  form = new FormGroup({
    isRestricted: new FormControl(false),
    projects: new FormControl({}),
    users: new FormControl({}),
  })
  hasErrorLoadingProjects = false
  hasErrorLoadingUsers = false
  hasHiddenProjects = false
  hasProjects = false
  hasUsers = false
  initialCachedValue: CachedValue
  isLoadingProjects = false
  isLoadingUsers = false
  nextAfterProjectId: string = null
  nextAfterUserId: string = null
  isSaving = false
  isDeveloper = false
  isSiteAdmin = false
  groupLabels: Record<string, string> = {}
  groupOptions: Option[] = []
  label: string
  loadedProjectRows: Record<string, ProjectRow> = {}
  loadedUserRows: Record<string, UserRow> = {}
  loadedProjects: ProjectWithStats[] = []
  loadedUsers: User[] = []
  projects$: Observable<ProjectWithStats[]>
  projectsColumns = ['checkbox', 'label', 'parents.group']
  selectedProjectLabels: string[] = []
  selectedUserLabels: string[] = []
  series: GearSeries
  siteAdminHiddenSelectedProjects = 0
  siteDeveloperHiddenSelectedProjects = 0
  tracked: TrackedGroups = {
    all: {
      projects: {},
      users: {},
    },
    filtered: {
      projects: {},
      users: {},
    },
  }
  tabIndex: number = null
  users$: Observable<User[]>
  usersColumns = ['checkbox', 'user']
  usersMap: Record<string, User> = {}

  readonly projectFilters = new FormGroup({ projects: new FormControl(''), groups: new FormControl([]) })
  readonly userFilters = new FormGroup({ users: new FormControl('') })
  readonly projectSort$ = new BehaviorSubject<Sort>({ active: 'label', direction: 'asc' })
  readonly userSort$ = new BehaviorSubject<Sort>({ active: 'user', direction: 'asc' })
  readonly loadMoreProjects$ = new Subject<void>()
  readonly loadMoreUsers$ = new Subject<void>()
  readonly projectRows$ = new BehaviorSubject<ProjectRow[]>([])
  readonly userRows$ = new BehaviorSubject<UserRow[]>([])

  private subscriptions = {
    label: Subscription.EMPTY,
    load: Subscription.EMPTY,
    projects: Subscription.EMPTY,
    save: Subscription.EMPTY,
    user: Subscription.EMPTY,
    users: Subscription.EMPTY,
  }

  @ViewChild('loadProjectsTrigger') loadProjectsTrigger: ElementRef
  @ViewChild('loadUsersTrigger') loadUsersTrigger: ElementRef

  constructor(
    @Inject(MAT_DIALOG_DATA) data: { label: string, series: GearSeries } = { label: '', series: {} as GearSeries },
    private coreApi: CoreApiService,
    private gearService: GearService,
    private logger: LoggingService,
    private notifications: NotificationService,
    private profileService: ProfileService,
    private projectsService: ProjectsService,
  ) {
    this.series = data.series || {} as GearSeries;
    this.label = data.label || '';
  }

  ngOnInit(): void {
    this.isLoadingProjects = true;
    this.isLoadingUsers = true;
    this.subscriptions.load = combineLatest([
      this.profileService.getProfile().pipe(filter((data) => Boolean(data))),
      this.projectsService.getGroups(),
      this.projectsService.getProjects(),
      this.projectsService.getUsers(),
    ]).subscribe(([profile, groups, projects, users]) => {
      this.isSiteAdmin = Boolean(profile && SiteRole.isSiteAdmin(profile));
      this.isDeveloper = Boolean(profile && SiteRole.isDeveloper(profile));
      if (!this.isSiteAdmin && !this.isDeveloper) {
        this.form.get('isRestricted').disable();
      }
      for (const group of groups) {
        this.groupLabels[group._id] = group.label;
        this.groupOptions.push({ value: group._id, label: group.label });
      }
      const projectIds = {};
      for (const id of (this.series.permissions?.projects || [])) {
        projectIds[id] = true;
      }
      this.projects$ = of(projects);
      this.trackItems(projects, 'all', 'projects', Object.keys(projectIds));
      const userIds = {};
      for (const id of (this.series.permissions?.users || [])) {
        userIds[id] = true;
      }
      this.users$ = of(users);
      this.trackItems(users, 'all', 'users', Object.keys(userIds));
      this.form.valueChanges.subscribe((value) => {
        this.setSelectedLabels(Object.keys(value.projects), 'projects');
        this.setSelectedLabels(Object.keys(value.users), 'users');
        const hiddenProjectsCount = this.getSelectedProjectIds().length - this.selectedProjectLabels.length;
        if (this.isSiteAdmin) {
          this.siteAdminHiddenSelectedProjects = hiddenProjectsCount;
        } else if (this.isDeveloper) {
          this.siteDeveloperHiddenSelectedProjects = hiddenProjectsCount;
        }
        this.hasHiddenProjects = hiddenProjectsCount > 0;
      });
      this.form.setValue({
        isRestricted: this.series.is_restricted || false,
        projects: projectIds,
        users: userIds,
      });
      this.initialCachedValue = JSON.parse(JSON.stringify(this.form.value));
      this.initProjects();
      this.initUsers();
    });
  }

  ngOnDestroy(): void {
    for (const subscription of Object.values(this.subscriptions)) {
      subscription.unsubscribe();
    }
  }

  get isRestricted(): boolean {
    // disabled control values are omitted from form.value
    return this.form.getRawValue().isRestricted || false;
  }

  onTabChange(event: MatTabChangeEvent): void {
    this.tabIndex = event.index;
  }

  hasChanged(): boolean {
    return !isEqual(this.initialCachedValue, this.form.getRawValue());
  }

  reset(): void {
    this.form.setValue(JSON.parse(JSON.stringify(this.initialCachedValue)));
    for (const [id, project] of Object.entries(this.tracked.all.projects)) {
      project.selected = Object.keys(this.form.value.projects).includes(id);
    }
    this.refreshCheckboxState('projects');
    this.clearProjectFilters();
    for (const [id, user] of Object.entries(this.tracked.all.users)) {
      user.selected = Object.keys(this.form.value.users).includes(id);
    }
    this.refreshCheckboxState('users');
    this.clearUserFilters();
  }

  clearProjectFilters(): void {
    this.projectFilters.get('projects').setValue('');
    this.projectFilters.get('groups').setValue([]);
  }

  clearUserFilters(): void {
    this.userFilters.get('users').setValue('');
  }

  save(): void {
    this.isSaving = true;
    this.subscriptions.save = this.gearService.updateGearAccess(
      { is_restricted: this.form.value.isRestricted, name: this.series.name } as GearSeries,
      { projects: this.getSelectedProjectIds(), users: this.getSelectedUserIds() } as GearPermissions,
    ).pipe(finalize(() => this.isSaving = false)).subscribe(() => {
      this.initialCachedValue = this.form.value as CachedValue;
      this.gearService.reloadGear$.next(this.series.name);
    });
  }

  getElementId(tag: string): string {
    return `${this.elementId}-${tag}`;
  }

  getSelectedProjectIds(): string[] {
    return Object.keys(this.form.value.projects);
  }

  getSelectedUserIds(): string[] {
    return Object.keys(this.form.value.users);
  }

  getFilterString(filterValues: { projects?: string, groups?: string[], users?: string }): string {
    const queryFilter = [];
    if (filterValues.projects) {
      queryFilter.push(`label=~${filterValues.projects}`);
    }
    if (filterValues.groups?.length > 0) {
      queryFilter.push(`group=|[${filterValues.groups.join(',')}]`);
    }
    if (filterValues.users) {
      queryFilter.push(`[firstname,lastname]=~${filterValues.users}`);
    }
    return queryFilter.join(',');
  }

  getQueryParams(filters, sort): ApiParams {
    const queryParams: ApiParams = {};
    const filterString = this.getFilterString(filters);
    if (filterString) {
      queryParams.filter = filterString;
    }
    const sortDirection = sort.direction || 'asc';
    queryParams.sort = sort.active === 'user'
      ? `firstname:${sortDirection},lastname:${sortDirection}`
      : `${sort.active}:${sortDirection}`;
    return queryParams;
  }

  setSelectedLabels(ids: string[], tab: 'projects' | 'users'): void {
    const selectedLabels = [];
    for (const id of ids) {
      const label = this.tracked.all[tab][id]?.label;
      if (label) {
        selectedLabels.push(label);
      }
    }
    this[tab === 'projects' ? 'selectedProjectLabels' : 'selectedUserLabels'] = selectedLabels;
  }

  clearProjectsView(): void {
    this.loadedProjectRows = {};
    this.loadedProjects = [];
    this.nextAfterProjectId = null;
    this.projectRows$.next([]);
  }

  clearUsersView(): void {
    this.loadedUserRows = {};
    this.loadedUsers = [];
    this.nextAfterUserId = null;
    this.userRows$.next([]);
  }

  trackItems(items: ProjectWithStats[] | User[], group: 'all' | 'filtered', tab: 'projects' | 'users', initIds?: string[]): void {
    const selectedIds = tab === 'projects' ? this.getSelectedProjectIds() : this.getSelectedUserIds();
    const tracking = this.tracked[group][tab] = {};
    for (const item of items) {
      // items between 'filtered' and 'all' should share the same reference
      tracking[item._id] = group === 'filtered' ? this.tracked.all[tab][item._id] : {
        label: tab === 'projects' ? (item as ProjectWithStats).label : `${(item as User).firstname} ${(item as User).lastname}`,
        selected: (initIds || selectedIds).includes(item._id),
      };
    }
  }

  initProjects(): void {
    combineLatest([
      replayValue(this.projectFilters).pipe(debounceTime(1000), tap(() => this.clearProjectsView())),
      this.projectSort$.pipe(tap(() => this.clearProjectsView())),
      this.loadMoreProjects$.pipe(startWith(undefined as void)),
    ]).subscribe(([filters, sort]) => {
      this.isLoadingProjects = true;
      this.projects$.pipe(
        map((projects) => queryResource<ProjectWithStats>(projects, { ...this.getQueryParams(filters, sort) }).results),
        tap((projects) => { this.trackItems(projects, 'filtered', 'projects'); }),
        map((projects) => queryResource<ProjectWithStats>(projects, {
          ...{ limit: 20 },
          ...(this.nextAfterProjectId ? { after_id: this.nextAfterProjectId } : {}),
        })),
      ).subscribe({
        next: (newPage) => {
          this.loadedProjects = this.loadedProjects.concat(newPage.results);
          this.nextAfterProjectId = newPage.total > newPage.count
            ? newPage.results[newPage.results.length - 1]?._id
            : null;
          this.isLoadingProjects = false;
          this.hasProjects = this.loadedProjects.length > 0;
          const projectRows = this.loadedProjects.map((project) => this.buildProjectRow(project));
          for (const row of projectRows) {
            this.loadedProjectRows[row._id] = row;
          }
          this.refreshCheckboxState('projects');
          this.projectRows$.next(projectRows);
        },
        error: (error) => {
          this.hasErrorLoadingProjects = true;
          this.isLoadingProjects = false;
          this.logger.error(error);
          this.notifications.error('projects.load.error');
        },
      });
    });
  }

  initUsers(): void {
    combineLatest([
      replayValue(this.userFilters).pipe(debounceTime(1000), tap(() => this.clearUsersView())),
      this.userSort$.pipe(tap(() => this.clearUsersView())),
      this.loadMoreUsers$.pipe(startWith(undefined as void)),
    ]).subscribe(([filters, sort]) => {
      this.isLoadingUsers = true;
      this.users$.pipe(
        map((users) => queryResource<User>(users, { ...this.getQueryParams(filters, sort) }).results),
        tap((users) => { this.trackItems(users, 'filtered', 'users'); }),
        map((users) => queryResource<User>(users, {
          ...{ limit: 20 },
          ...(this.nextAfterUserId ? { after_id: this.nextAfterUserId } : {}),
        })),
      ).subscribe({
        next: (newPage) => {
          this.loadedUsers = this.loadedUsers.concat(newPage.results);
          this.nextAfterUserId = newPage.total > newPage.count
            ? newPage.results[newPage.results.length - 1]?._id
            : null;
          this.isLoadingUsers = false;
          this.hasUsers = this.loadedUsers.length > 0;
          for (const user of this.loadedUsers) {
            this.usersMap[user._id] = user;
          }
          const userRows = this.loadedUsers.map((user) => this.buildUserRow(user));
          for (const row of userRows) {
            this.loadedUserRows[row._id] = row;
          }
          this.refreshCheckboxState('users');
          this.userRows$.next(userRows);
        },
        error: (error) => {
          this.hasErrorLoadingUsers = true;
          this.isLoadingUsers = false;
          this.logger.error(error);
          this.notifications.error('users.load.error');
        },
      });
    });
  }

  loadMore(tab: 'projects' | 'users'): void {
    // ensures continuous loading in the case the trigger element is still visible after initial fetch for more data
    const triggerElement = (tab === 'projects' ? this.loadProjectsTrigger : this.loadUsersTrigger).nativeElement;
    const triggerTop = triggerElement.getBoundingClientRect().top;
    const triggerParentBottom = triggerElement.closest('.mat-tab-body-content')?.getBoundingClientRect().bottom;
    let triggerIsVisible = false;
    if (!isNaN(triggerTop) && !isNaN(triggerParentBottom)) {
      triggerIsVisible = triggerTop < triggerParentBottom;
    }
    if (tab === 'projects' && !this.isLoadingProjects && this.nextAfterProjectId && triggerIsVisible) {
      this.loadMoreProjects$.next();
    }
    if (tab === 'users' && !this.isLoadingUsers && this.nextAfterUserId && triggerIsVisible) {
      this.loadMoreUsers$.next();
    }
  }

  buildProjectRow(project: Project): ProjectRow {
    return {
      _id: project._id,
      label: project.label,
      group: this.groupLabels[project.group],
    };
  }

  buildUserRow(user: User): UserRow {
    return {
      _id: user._id,
      user: `${user.firstname} ${user.lastname}`,
    };
  }

  selectItem(checked: boolean, id: string, tab: 'projects' | 'users'): void {
    this.tracked.all[tab][id].selected = checked;
    const selected = {};
    for (const [key, item] of Object.entries(this.tracked.all[tab])) {
      if (item.selected) {
        selected[key] = true;
      }
    }
    this.form.get(tab).setValue(selected);
    this.refreshCheckboxState(tab);
  }

  selectAllItems(checked: boolean, tab: 'projects' | 'users'): void {
    const selected = {};
    for (const [key, item] of Object.entries(this.tracked.filtered[tab])) {
      item.selected = checked;
      if (item.selected) {
        selected[key] = true;
      }
    }
    this.form.get(tab).setValue(selected);
    this.refreshCheckboxState(tab);
  }

  refreshCheckboxState(tab: 'projects' | 'users'): void {
    this.checkAllState[tab].some = false;
    this.checkAllState[tab].all = false;
    const selected = [];
    const notSelected = [];
    for (const item of Object.values(this.tracked.filtered[tab])) {
      if (item.selected) {
        selected.push(item);
      } else {
        notSelected.push(item);
      }
      if (selected.length > 0 && notSelected.length > 0) {
        this.checkAllState[tab].some = true;
        return;
      }
    }
    this.checkAllState[tab].all = selected.length > 0;
  }
}
